import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Loader2, AlertCircle, ShieldAlert, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { suggestNewAllergens } from "../lib/allergens";

// 14 EU allergens (règlement INCO)
export const EU_ALLERGENS = [
  "Gluten", "Œufs", "Lait", "Poisson", "Crustacés", "Arachides",
  "Fruits à coque", "Soja", "Céleri", "Moutarde", "Sésame",
  "Sulfites", "Lupin", "Mollusques",
];

const fetchAgromanageProducts = async () => {
  const { data } = await api.get("/integration/agromanage/products");
  return data;
};

export const AgroFieldsPanel = ({ sku, onSkuChange, allergens, onAllergensChange, ingredients = [] }) => {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [skuOpen, setSkuOpen] = useState(false);
  const [skuFilter, setSkuFilter] = useState("");

  const suggestions = useMemo(
    () => suggestNewAllergens(ingredients, allergens),
    [ingredients, allergens]
  );

  useEffect(() => {
    let mounted = true;
    setLoadingProducts(true);
    fetchAgromanageProducts()
      .then((data) => {
        if (mounted) {
          setProducts(data || []);
          setProductsError(null);
        }
      })
      .catch((e) => {
        if (mounted) {
          const detail = e?.response?.data?.detail || "Impossible de joindre AgroManage.";
          setProductsError(detail);
          setProducts([]);
        }
      })
      .finally(() => mounted && setLoadingProducts(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filteredProducts = skuFilter
    ? products.filter(
        (p) =>
          p.sku.toLowerCase().includes(skuFilter.toLowerCase()) ||
          (p.name || "").toLowerCase().includes(skuFilter.toLowerCase()),
      )
    : products;

  const toggleAllergen = (a) => {
    if (allergens.includes(a)) {
      onAllergensChange(allergens.filter((x) => x !== a));
    } else {
      onAllergensChange([...allergens, a]);
    }
  };

  const skuAvailable = !productsError && products.length > 0;

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm"
      data-testid="agro-fields-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold text-stone-900">
          Liaison AgroManage
        </h2>
        <span className="text-xs text-stone-400 uppercase tracking-widest font-bold">
          Étiquetage
        </span>
      </div>

      {/* SKU field */}
      <div className="mb-5">
        <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-2">
          SKU produit AgroManage <span className="text-red-500 normal-case font-normal">*</span>
        </label>

        {!sku && !loadingProducts && (
          <div
            className="mb-2 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-700"
            data-testid="sku-required-warning"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              <strong>SKU obligatoire</strong> — la recette ne peut pas être enregistrée tant qu&apos;aucun
              SKU AgroManage n&apos;est sélectionné.
            </span>
          </div>
        )}

        {loadingProducts && (
          <div className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-md text-sm text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Récupération de la liste AgroManage…
          </div>
        )}

        {!loadingProducts && skuAvailable && (
          <Popover open={skuOpen} onOpenChange={setSkuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 border border-stone-200 rounded-md bg-white hover:border-emerald-400 transition-colors text-left text-sm"
                data-testid="sku-select-trigger"
              >
                <span className={sku ? "font-semibold text-stone-900" : "text-stone-400"}>
                  {sku
                    ? `${sku}${products.find((p) => p.sku === sku)?.name ? ` — ${products.find((p) => p.sku === sku).name}` : ""}`
                    : "Choisir un SKU…"}
                </span>
                <ChevronDown className="h-4 w-4 text-stone-400 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
              <div className="p-2 border-b border-stone-100">
                <Input
                  value={skuFilter}
                  onChange={(e) => setSkuFilter(e.target.value)}
                  placeholder="Filtrer par SKU ou nom…"
                  className="h-8 text-sm"
                  data-testid="sku-filter-input"
                />
              </div>
              <div className="max-h-72 overflow-y-auto scrollbar-thin py-1">
                {filteredProducts.length === 0 && (
                  <div className="px-3 py-4 text-sm text-stone-500 text-center">
                    Aucun produit.
                  </div>
                )}
                {filteredProducts.map((p) => (
                  <button
                    key={p.sku}
                    type="button"
                    onClick={() => {
                      onSkuChange(p.sku);
                      setSkuOpen(false);
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-emerald-50 text-left text-sm transition-colors"
                    data-testid={`sku-option-${p.sku}`}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-stone-900">{p.sku}</div>
                      {p.name && (
                        <div className="text-xs text-stone-500 truncate">{p.name}</div>
                      )}
                    </div>
                    {sku === p.sku && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
                  </button>
                ))}
              </div>
              {sku && (
                <button
                  type="button"
                  onClick={() => {
                    onSkuChange("");
                    setSkuOpen(false);
                  }}
                  className="w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50 border-t border-stone-100 text-left transition-colors"
                >
                  Désélectionner
                </button>
              )}
            </PopoverContent>
          </Popover>
        )}

        {!loadingProducts && !skuAvailable && (
          <>
            <Input
              value={sku || ""}
              onChange={(e) => onSkuChange(e.target.value)}
              placeholder="Saisir le SKU (ex: P12345)"
              className="border-stone-200 focus-visible:ring-emerald-500"
              data-testid="sku-text-input"
            />
            <div
              className="mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800"
              data-testid="sku-fallback-warning"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {productsError || "Liste AgroManage indisponible."}{" "}
                Saisissez le SKU manuellement pour le moment.
              </span>
            </div>
          </>
        )}
      </div>

      {/* Allergens */}
      <div>
        <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-2">
          Allergènes présents <span className="font-normal lowercase tracking-normal text-stone-400">(14 allergènes UE)</span>
        </label>

        {suggestions.length > 0 && (
          <div
            className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
            data-testid="allergen-suggestions"
          >
            <div className="flex items-start gap-2 mb-2">
              <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 flex-1">
                <strong>Détection automatique</strong> à partir des ingrédients :{" "}
                {suggestions.length} allergène{suggestions.length > 1 ? "s" : ""} potentiel
                {suggestions.length > 1 ? "s" : ""} non coché{suggestions.length > 1 ? "s" : ""}.
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  onAllergensChange([
                    ...allergens,
                    ...suggestions.map((s) => s.allergen),
                  ])
                }
                className="h-7 px-3 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                data-testid="apply-all-allergen-suggestions"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Tout cocher
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 pl-6">
              {suggestions.map((s) => (
                <button
                  key={s.allergen}
                  type="button"
                  onClick={() => onAllergensChange([...allergens, s.allergen])}
                  className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 transition-colors flex items-center gap-1"
                  title={`Détecté dans : ${s.evidence.slice(0, 3).join(", ")}`}
                  data-testid={`allergen-suggestion-${s.allergen}`}
                >
                  <span>+</span>
                  {s.allergen}
                  <span className="text-amber-500 text-[10px]">
                    ({s.evidence.length})
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="allergens-list">
          {EU_ALLERGENS.map((a) => {
            const selected = allergens.includes(a);
            return (
              <button
                key={a}
                type="button"
                onClick={() => toggleAllergen(a)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  selected
                    ? "bg-emerald-700 border-emerald-700 text-white shadow-sm"
                    : "bg-white border-stone-200 text-stone-700 hover:border-emerald-400"
                }`}
                data-testid={`allergen-${a}`}
              >
                {selected && <Check className="h-3 w-3 inline mr-1 -mt-0.5" />}
                {a}
              </button>
            );
          })}
        </div>
        {allergens.length > 0 && (
          <div className="mt-2 text-xs text-stone-500">
            {allergens.length} allergène{allergens.length > 1 ? "s" : ""} sélectionné
            {allergens.length > 1 ? "s" : ""}.
          </div>
        )}
      </div>
    </div>
  );
};

export default AgroFieldsPanel;
